import React, { useState, useEffect } from 'react';
import {
    getGroups,
    createGroup,
    removeGroup,
    setUserAdmin,
    removeUserAdmin,
    updateGroupTitle,
    updateGroup,
    WalkingGroup,
    CreateGroupRequest,
    SetUserAdminRequest,
    RemoveUserAdminRequest
} from '../../api/walking-groups-admin';

const WalkingGroupsAdmin = () => {
    const [groups, setGroups] = useState<WalkingGroup[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Filters
    const [titleFilter, setTitleFilter] = useState('');
    const [postcodeFilter, setPostcodeFilter] = useState('');
    const [missingAdminOnly, setMissingAdminOnly] = useState(false);

    // Create group form
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [createFormData, setCreateFormData] = useState({
        postcodePrefix: '',
        title: '',
        defaultLat: '',
        defaultLong: ''
    });

    // Selected group for details
    const [selectedGroup, setSelectedGroup] = useState<WalkingGroup | null>(null);
    const [editingTitle, setEditingTitle] = useState(false);
    const [editTitleValue, setEditTitleValue] = useState('');
    const [editingDefaultLocation, setEditingDefaultLocation] = useState(false);
    const [editDefaultLatValue, setEditDefaultLatValue] = useState('');
    const [editDefaultLongValue, setEditDefaultLongValue] = useState('');

    // Add/remove admin
    const [adminUserId, setAdminUserId] = useState('');

    const parsePostcodePrefixes = (input: string): string[] => {
        return input
            .split(',')
            .map(value => value.trim().toUpperCase())
            .filter(value => value.length > 0);
    };

    const formatPostcodePrefixes = (prefixes: string[]): string => {
        return prefixes.length > 0 ? prefixes.join(', ') : '—';
    };

    useEffect(() => {
        loadGroups();
    }, [titleFilter, postcodeFilter, missingAdminOnly]);

    const loadGroups = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await getGroups({
                title: titleFilter || undefined,
                postcodePrefix: postcodeFilter || undefined,
                missingAdmin: missingAdminOnly || undefined
            });
            setGroups(response.groups);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateGroup = async (e: React.FormEvent) => {
        e.preventDefault();
        const postcodePrefixes = parsePostcodePrefixes(createFormData.postcodePrefix);
        if (postcodePrefixes.length === 0 || !createFormData.title.trim()) {
            setError('Please fill in all fields');
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');

        try {
            const parseNum = (v: string | number): number | undefined => {
                if (v === '' || v === undefined) return undefined;
                const n = Number(v);
                return isNaN(n) ? undefined : n;
            };
            const title = createFormData.title.trim();
            const defaultLat = parseNum(createFormData.defaultLat);
            const defaultLong = parseNum(createFormData.defaultLong);

            const request: CreateGroupRequest = {
                postcodePrefixes,
                title,
                defaultLat,
                defaultLong
            };
            await createGroup(request);
            setSuccess('Group created successfully');
            setCreateFormData({ postcodePrefix: '', title: '', defaultLat: '', defaultLong: '' });
            setShowCreateForm(false);
            loadGroups();
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveGroup = async (groupId: string) => {
        if (!window.confirm('Are you sure you want to remove this group? This action cannot be undone.')) {
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');

        try {
            await removeGroup(groupId);
            setSuccess('Group removed successfully');
            if (selectedGroup?.groupId === groupId) {
                setSelectedGroup(null);
            }
            loadGroups();
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSetUserAdmin = async (groupId: string) => {
        if (!adminUserId.trim()) {
            setError('Please enter a user ID');
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');

        try {
            const request: SetUserAdminRequest = {
                userId: adminUserId.trim(),
                groupId: groupId
            };
            await setUserAdmin(request);
            setSuccess('User set as admin successfully');
            setAdminUserId('');
            if (selectedGroup?.groupId === groupId) {
                loadGroups();
                const updatedGroups = await getGroups();
                const updatedGroup = updatedGroups.groups.find(g => g.groupId === groupId);
                if (updatedGroup) {
                    setSelectedGroup(updatedGroup);
                }
            } else {
                loadGroups();
            }
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveUserAdmin = async (groupId: string, userId: string) => {
        if (!window.confirm(`Are you sure you want to remove this user as admin?`)) {
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');

        try {
            const request: RemoveUserAdminRequest = {
                userId: userId,
                groupId: groupId
            };
            await removeUserAdmin(request);
            setSuccess('Admin removed successfully');
            if (selectedGroup?.groupId === groupId) {
                loadGroups();
                const updatedGroups = await getGroups();
                const updatedGroup = updatedGroups.groups.find(g => g.groupId === groupId);
                if (updatedGroup) {
                    setSelectedGroup(updatedGroup);
                }
            } else {
                loadGroups();
            }
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateTitle = async (groupId: string) => {
        if (!editTitleValue.trim()) {
            setError('Title cannot be empty');
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');

        try {
            await updateGroupTitle(groupId, { title: editTitleValue.trim() });
            setSuccess('Title updated successfully');
            setEditingTitle(false);
            loadGroups();
            const updatedGroups = await getGroups();
            const updatedGroup = updatedGroups.groups.find(g => g.groupId === groupId);
            if (updatedGroup) {
                setSelectedGroup(updatedGroup);
            }
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateDefaultLocation = async (groupId: string) => {
        const parseNum = (v: string): number | null => {
            if (v.trim() === '') return null;
            const n = Number(v);
            return isNaN(n) ? null : n;
        };

        setLoading(true);
        setError('');
        setSuccess('');

        try {
            await updateGroup(groupId, {
                defaultLat: parseNum(editDefaultLatValue),
                defaultLong: parseNum(editDefaultLongValue)
            });
            setSuccess('Default location updated successfully');
            setEditingDefaultLocation(false);
            loadGroups();
            const updatedGroups = await getGroups();
            const updatedGroup = updatedGroups.groups.find(g => g.groupId === groupId);
            if (updatedGroup) setSelectedGroup(updatedGroup);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const openGroupDetails = async (group: WalkingGroup) => {
        setSelectedGroup(group);
        setEditTitleValue(group.title);
        setEditingTitle(false);
        setEditDefaultLatValue(group.defaultLat != null ? String(group.defaultLat) : '');
        setEditDefaultLongValue(group.defaultLong != null ? String(group.defaultLong) : '');
        setEditingDefaultLocation(false);
        setAdminUserId('');
    };

    return (
        <div className="max-w-7xl mx-auto py-8">
            <div className="bg-white shadow-lg rounded-lg p-6">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-800">Walking Groups Administration</h2>
                    <button
                        onClick={() => setShowCreateForm(!showCreateForm)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                        {showCreateForm ? 'Cancel' : 'Create Group'}
                    </button>
                </div>

                {/* Messages */}
                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                        <p className="text-sm text-red-800">{error}</p>
                    </div>
                )}

                {success && (
                    <div className="bg-green-50 border border-green-200 rounded-md p-3 mb-4">
                        <p className="text-sm text-green-800">{success}</p>
                    </div>
                )}

                {/* Create Group Form */}
                {showCreateForm && (
                    <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                        <h3 className="text-lg font-semibold mb-4">Create New Group</h3>
                        <p className="text-sm text-gray-600 mb-4">
                            Allowed format: comma-separated postcode prefixes like SW1 or SW1, SW2.
                        </p>
                        <form onSubmit={handleCreateGroup} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Postcode Prefix *
                                </label>
                                <input
                                    type="text"
                                    value={createFormData.postcodePrefix}
                                    onChange={(e) => setCreateFormData({ ...createFormData, postcodePrefix: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="e.g., SW1 or SW1, SW2"
                                    disabled={loading}
                                    required
                                />
                                <p className="text-xs text-gray-500 mt-1">Comma-separated prefixes allowed; each must be unique and not overlap</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Title *
                                </label>
                                <input
                                    type="text"
                                    value={createFormData.title}
                                    onChange={(e) => setCreateFormData({ ...createFormData, title: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Group title"
                                    disabled={loading}
                                    required
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Default latitude (optional)
                                    </label>
                                    <input
                                        type="number"
                                        step="any"
                                        value={createFormData.defaultLat}
                                        onChange={(e) => setCreateFormData({ ...createFormData, defaultLat: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="e.g. 51.5074"
                                        disabled={loading}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Default longitude (optional)
                                    </label>
                                    <input
                                        type="number"
                                        step="any"
                                        value={createFormData.defaultLong}
                                        onChange={(e) => setCreateFormData({ ...createFormData, defaultLong: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="e.g. -0.1278"
                                        disabled={loading}
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end space-x-2">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowCreateForm(false);
                                        setCreateFormData({ postcodePrefix: '', title: '', defaultLat: '', defaultLong: '' });
                                    }}
                                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                                >
                                    {loading ? 'Creating...' : 'Create Group'}
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Groups List */}
                    <div className="lg:col-span-2">
                        <div className="mb-4 space-y-3">
                            <h3 className="text-lg font-semibold">Groups</h3>
                            
                            {/* Filters */}
                            <div className="flex flex-wrap gap-2">
                                <input
                                    type="text"
                                    value={titleFilter}
                                    onChange={(e) => setTitleFilter(e.target.value)}
                                    className="flex-1 min-w-[200px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Filter by title..."
                                />
                                <input
                                    type="text"
                                    value={postcodeFilter}
                                    onChange={(e) => setPostcodeFilter(e.target.value)}
                                    className="flex-1 min-w-[200px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Filter by postcode prefix..."
                                />
                                <p className="text-xs text-gray-500 self-center">
                                    Comma-separated prefixes, e.g. SW1, SW2
                                </p>
                                <label className="flex items-center space-x-2 px-3 py-2">
                                    <input
                                        type="checkbox"
                                        checked={missingAdminOnly}
                                        onChange={(e) => setMissingAdminOnly(e.target.checked)}
                                        className="rounded"
                                    />
                                    <span className="text-sm">Missing admin only</span>
                                </label>
                            </div>
                        </div>

                        {loading && groups.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">Loading groups...</div>
                        ) : groups.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">No groups found</div>
                        ) : (
                            <div className="space-y-2">
                                {groups.map((group) => (
                                    <div
                                        key={group.groupId}
                                        className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                                            selectedGroup?.groupId === group.groupId
                                                ? 'bg-blue-50 border-blue-300'
                                                : 'bg-white border-gray-200 hover:bg-gray-50'
                                        }`}
                                        onClick={() => openGroupDetails(group)}
                                    >
                                        <div className="flex justify-between items-start">
                                            <div className="flex-1">
                                                <h4 className="font-semibold text-gray-800">{group.title}</h4>
                                                <p className="text-sm text-gray-600">
                                                    Postcode prefixes: {formatPostcodePrefixes(group.postcodePrefixes)}
                                                </p>
                                                <p className="text-xs text-gray-500 mt-1">
                                                    {group.adminUserIds.length} admin(s), {group.memberUserIds.length} member(s)
                                                </p>
                                                {group.adminUserIds.length === 0 && (
                                                    <span className="inline-block mt-1 px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded">
                                                        No admins
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Group Details */}
                    <div className="lg:col-span-1">
                        {selectedGroup ? (
                            <div className="bg-gray-50 rounded-lg p-4 sticky top-4">
                                <div className="flex justify-between items-start mb-4">
                                    <h3 className="text-lg font-semibold">Group Details</h3>
                                    <button
                                        onClick={() => setSelectedGroup(null)}
                                        className="text-gray-500 hover:text-gray-700"
                                    >
                                        ✕
                                    </button>
                                </div>

                                <div className="space-y-4">
                                    {/* Title */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                                        {editingTitle ? (
                                            <div className="flex space-x-2">
                                                <input
                                                    type="text"
                                                    value={editTitleValue}
                                                    onChange={(e) => setEditTitleValue(e.target.value)}
                                                    className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                                                    disabled={loading}
                                                />
                                                <button
                                                    onClick={() => handleUpdateTitle(selectedGroup.groupId)}
                                                    disabled={loading}
                                                    className="px-2 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                                                >
                                                    Save
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setEditingTitle(false);
                                                        setEditTitleValue(selectedGroup.title);
                                                    }}
                                                    className="px-2 py-1 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="flex justify-between items-center">
                                                <p className="text-sm text-gray-900">{selectedGroup.title}</p>
                                                <button
                                                    onClick={() => setEditingTitle(true)}
                                                    className="text-xs text-blue-600 hover:text-blue-800"
                                                >
                                                    Edit
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    {/* Postcode */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Postcode Prefixes</label>
                                        <p className="text-sm text-gray-900 font-mono">
                                            {formatPostcodePrefixes(selectedGroup.postcodePrefixes)}
                                        </p>
                                    </div>

                                    {/* Default location */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Default location</label>
                                        {editingDefaultLocation ? (
                                            <div className="space-y-2">
                                                <div className="flex gap-2">
                                                    <input
                                                        type="number"
                                                        step="any"
                                                        value={editDefaultLatValue}
                                                        onChange={(e) => setEditDefaultLatValue(e.target.value)}
                                                        className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                                                        placeholder="Latitude"
                                                        disabled={loading}
                                                    />
                                                    <input
                                                        type="number"
                                                        step="any"
                                                        value={editDefaultLongValue}
                                                        onChange={(e) => setEditDefaultLongValue(e.target.value)}
                                                        className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                                                        placeholder="Longitude"
                                                        disabled={loading}
                                                    />
                                                </div>
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => handleUpdateDefaultLocation(selectedGroup.groupId)}
                                                        disabled={loading}
                                                        className="px-2 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                                                    >
                                                        Save
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setEditingDefaultLocation(false);
                                                            setEditDefaultLatValue(selectedGroup.defaultLat != null ? String(selectedGroup.defaultLat) : '');
                                                            setEditDefaultLongValue(selectedGroup.defaultLong != null ? String(selectedGroup.defaultLong) : '');
                                                        }}
                                                        className="px-2 py-1 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400"
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="flex justify-between items-center">
                                                <p className="text-sm text-gray-900">
                                                    {(selectedGroup.defaultLat != null && selectedGroup.defaultLong != null)
                                                        ? `${selectedGroup.defaultLat}, ${selectedGroup.defaultLong}`
                                                        : 'Not set'}
                                                </p>
                                                <button
                                                    onClick={() => setEditingDefaultLocation(true)}
                                                    className="text-xs text-blue-600 hover:text-blue-800"
                                                >
                                                    Edit
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    {/* Group ID */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Group ID</label>
                                        <p className="text-xs text-gray-600 font-mono break-all">{selectedGroup.groupId}</p>
                                    </div>

                                    {/* Admins */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Admins</label>
                                        {selectedGroup.adminUserIds.length === 0 ? (
                                            <p className="text-sm text-yellow-600">No admins assigned</p>
                                        ) : (
                                            <div className="space-y-1 mb-2">
                                                {selectedGroup.adminUserIds.map((userId) => (
                                                    <div key={userId} className="flex justify-between items-center bg-white p-2 rounded text-sm">
                                                        <span className="font-mono text-xs">{userId}</span>
                                                        <button
                                                            onClick={() => handleRemoveUserAdmin(selectedGroup.groupId, userId)}
                                                            disabled={loading}
                                                            className="text-red-600 hover:text-red-800 text-xs"
                                                        >
                                                            Remove
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        <div className="flex space-x-2 mt-2">
                                            <input
                                                type="text"
                                                value={adminUserId}
                                                onChange={(e) => setAdminUserId(e.target.value)}
                                                className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                                                placeholder="User ID"
                                                disabled={loading}
                                            />
                                            <button
                                                onClick={() => handleSetUserAdmin(selectedGroup.groupId)}
                                                disabled={loading || !adminUserId.trim()}
                                                className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                                            >
                                                Add
                                            </button>
                                        </div>
                                    </div>

                                    {/* Stats */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Statistics</label>
                                        <div className="text-sm text-gray-600 space-y-1">
                                            <p>Members: {selectedGroup.memberUserIds.length}</p>
                                        </div>
                                    </div>

                                    {/* Actions */}
                                    <div className="pt-4 border-t">
                                        <button
                                            onClick={() => handleRemoveGroup(selectedGroup.groupId)}
                                            disabled={loading}
                                            className="w-full px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                                        >
                                            Remove Group
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-gray-50 rounded-lg p-4 text-center text-gray-500">
                                Select a group to view details
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WalkingGroupsAdmin;

